import { Kind } from './constants';
import { GraphQLClientError, type GraphQLErrorResponse, RequestError } from './errors';
import { errorFilters } from './errors/filters';
import type { ClientConfig, Options, QueryOrMutation, Variables } from './types';

/**
 * A simple GraphQL client that sends queries and mutations to a GraphQL API.
 * It uses the Fetch API to make requests.
 */
export class GraphQLClient {
  private static readonly MAX_RETRIES = 3;

  /**
   * @param config The configuration for the client.
   */
  constructor(private readonly config: ClientConfig) {}

  private async request<Response = unknown, Vars extends Variables = Variables>(
    queryOrMutation: QueryOrMutation,
    variables?: Vars,
    options?: Options,
    shouldRetry = false,
    attempt = 0,
    requestId = crypto.randomUUID(),
  ): Promise<Response> {
    const { url, headers = {}, logger } = this.config;
    const nw_request_id = requestId;

    logger?.trace?.({ nw_request_id }, `Making request to ${url}`);

    try {
      let operationName = {};
      if (typeof queryOrMutation !== 'string') {
        for (const definition of queryOrMutation.definitions) {
          if (definition.kind === Kind.OPERATION_DEFINITION) {
            const name = definition?.name?.value;
            if (name) {
              operationName = {
                operationName: name,
              };
            }
          }
        }
      }

      const body = JSON.stringify({
        query:
          typeof queryOrMutation === 'string' ? queryOrMutation : queryOrMutation.loc?.source.body,
        variables,
        ...operationName,
      });

      const response = await fetch(url, {
        method: 'POST',
        body,
        headers: {
          // 'x-request-id' is used for tracing requests in the logs.
          'x-request-id': nw_request_id,
          ...headers,
        },
        credentials: 'include',
        ...options,
      });

      if (!response.ok) {
        throw new RequestError(`Status code: ${response.status} ${response.statusText}`, response);
      }

      logger?.trace?.({ nw_request_id }, `Received response with status code: ${response.status}`);

      const responseJson = (await response.clone().json()) as Response & GraphQLErrorResponse;

      if (responseJson.errors) {
        const errors = errorFilters.reduce(
          (acc, filter) => acc.filter(filter),
          responseJson.errors || [],
        );

        if (errors.length > 0) {
          throw new RequestError(
            `GraphQL errors: ${errors.map(err => err.message).join(', ')}`,
            response,
          );
        }
      }

      return responseJson;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger?.trace?.({ nw_request_id, error }, 'Request was aborted');

        throw new GraphQLClientError('Request was aborted', {
          cause: error,
        });
      }

      const errorMessage = (error as Error).message || 'Unknown error';
      const errorCause =
        error instanceof Error && error.cause instanceof Error
          ? error.cause.message
          : 'Unknown cause';

      if (shouldRetry && attempt < GraphQLClient.MAX_RETRIES) {
        const backoffMs = 500 * Math.pow(2, attempt) * (1 + Math.random() * 0.2);
        logger?.warn?.(
          {
            nw_request_id,
            attempt: attempt + 1,
            maxRetries: GraphQLClient.MAX_RETRIES,
            errorCause,
          },
          `[Retrying] Request failed with: ${errorMessage}`,
        );
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.request<Response>(
          queryOrMutation,
          variables,
          options,
          shouldRetry,
          attempt + 1,
          nw_request_id,
        );
      }

      if (error instanceof RequestError && error.name === 'RequestError' && error.response) {
        const responseJson = (await error.response.json()) as Response & GraphQLErrorResponse;

        const errors = errorFilters.reduce(
          (acc, filter) => acc.filter(filter),
          responseJson.errors || [],
        );

        const flatErrors = errors.map(err => {
          return {
            extensions: err.extensions,
            message: err.message,
          };
        });

        if (flatErrors.length > 0) {
          logger?.error?.({ nw_request_id, flatErrors }, `Request failed with: ${errorMessage}`);
        }

        delete error.response;

        throw new GraphQLClientError(`Failed to request GraphQL API. ${errorMessage}`, {
          cause: error instanceof Error ? error : undefined,
        });
      }

      logger?.error?.({ nw_request_id, error }, `[Uncaught] Request failed with: ${errorMessage}`);

      throw new GraphQLClientError(`Failed to request GraphQL API. ${errorMessage}`, {
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  /**
   * Send a query to the GraphQL API.
   * @param query
   * @param variables
   * @param options Additional options to pass to the Fetch API.
   * @returns A promise that resolves to the response from the API.
   */
  query<Response = unknown, Vars extends Variables = Variables>(
    query: QueryOrMutation,
    variables?: Vars,
    options?: Options,
  ): Promise<Response> {
    return this.request<Response>(query, variables, options, true);
  }

  /**
   * Send a mutation to the GraphQL API.
   * @param query
   * @param variables
   * @param options Additional options to pass to the Fetch API.
   * @returns A promise that resolves to the response from the API.
   */
  mutation<Response = unknown, Vars extends Variables = Variables>(
    query: QueryOrMutation,
    variables?: Vars,
    options?: Options,
  ): Promise<Response> {
    return this.request<Response>(query, variables, options);
  }
}
